import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ConnectionProvider, WalletProvider as SolanaWalletProvider, useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { clusterApiUrl } from '@solana/web3.js';
// Legacy adapters for wallets that don't support Wallet Standard
// Wallets that implement Wallet Standard (like Nightly, Phantom, Solflare) 
// are auto-detected and don't need explicit adapters
import { SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import { handleWalletConnection } from '../utils/referralTracking';
import { setSupabaseJWT, signOutSupabase } from '../lib/supabase';
import bs58 from 'bs58';

// Wallet adapters configuration
// Modern wallets (Phantom, Nightly, Solflare, Backpack, etc.) implement the Wallet Standard
// and are auto-detected by @solana/wallet-adapter-react - no explicit adapters needed!
// Only add legacy adapters here for wallets that DON'T support Wallet Standard.
const createWalletAdapters = () => {
  const adapters = [];
  
  // Legacy adapters only - Wallet Standard wallets are auto-detected
  // Most modern wallets (Phantom, Nightly, Solflare, Backpack, Glow, etc.)
  // implement Wallet Standard and will appear automatically
  
  // Uncomment below only if you need to support a legacy wallet:
  // try {
  //   adapters.push(new SolflareWalletAdapter());
  // } catch (e) {
  //   console.log('Solflare adapter not available');
  // }
  
  return adapters;
};

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Wallet type configurations
const WALLET_CONFIGS = {
  // Solana Wallets
  phantom: {
    name: 'Phantom',
    type: 'solana',
    icon: '👻',
    color: '#9945FF',
    downloadUrl: 'https://phantom.app/',
    detector: () => window.solana?.isPhantom,
    connector: () => window.solana,
  },
  solflare: {
    name: 'Solflare',
    type: 'solana',
    icon: '🔥',
    color: '#FC8D00',
    downloadUrl: 'https://solflare.com/',
    detector: () => window.solflare?.isSolflare,
    connector: () => window.solflare,
  },
  backpack: {
    name: 'Backpack',
    type: 'solana',
    icon: '🎒',
    color: '#E33E3F',
    downloadUrl: 'https://backpack.app/',
    detector: () => window.backpack?.isBackpack,
    connector: () => window.backpack,
  },
  glow: {
    name: 'Glow',
    type: 'solana',
    icon: '✨',
    color: '#FFD700',
    downloadUrl: 'https://glow.app/',
    detector: () => window.glow,
    connector: () => window.glow,
  },
  nightly: {
    name: 'Nightly',
    type: 'solana',
    icon: '🌙',
    color: '#6366F1',
    downloadUrl: 'https://nightly.app/',
    detector: () => window.nightly?.solana,
    connector: () => window.nightly?.solana,
  },
  slope: {
    name: 'Slope',
    type: 'solana',
    icon: '📐',
    color: '#6C5CE7',
    downloadUrl: 'https://slope.finance/',
    detector: () => window.Slope,
    connector: () => window.Slope,
  },
  sollet: {
    name: 'Sollet',
    type: 'solana',
    icon: '🔗',
    color: '#00D4AA',
    downloadUrl: 'https://www.sollet.io/',
    detector: () => window.sollet,
    connector: () => window.sollet,
  },
  
  // EVM Wallets
  metamask: {
    name: 'MetaMask',
    type: 'ethereum',
    icon: '🦊',
    color: '#F6851B',
    downloadUrl: 'https://metamask.io/',
    detector: () => window.ethereum?.isMetaMask,
    connector: () => window.ethereum,
  },
  coinbase: {
    name: 'Coinbase Wallet',
    type: 'ethereum',
    icon: '🔵',
    color: '#0052FF',
    downloadUrl: 'https://wallet.coinbase.com/',
    detector: () => window.ethereum?.isCoinbaseWallet,
    connector: () => window.ethereum,
  },
  walletconnect: {
    name: 'WalletConnect',
    type: 'ethereum',
    icon: '🔗',
    color: '#3B99FC',
    downloadUrl: 'https://walletconnect.com/',
    detector: () => window.ethereum?.isWalletConnect,
    connector: () => window.ethereum,
  },
  trust: {
    name: 'Trust Wallet',
    type: 'ethereum',
    icon: '🛡️',
    color: '#3375BB',
    downloadUrl: 'https://trustwallet.com/',
    detector: () => window.ethereum?.isTrust,
    connector: () => window.ethereum,
  },
  rainbow: {
    name: 'Rainbow',
    type: 'ethereum',
    icon: '🌈',
    color: '#FF6B6B',
    downloadUrl: 'https://rainbow.me/',
    detector: () => window.ethereum?.isRainbow,
    connector: () => window.ethereum,
  },
  brave: {
    name: 'Brave Wallet',
    type: 'ethereum',
    icon: '🦁',
    color: '#FB542B',
    downloadUrl: 'https://brave.com/wallet/',
    detector: () => window.ethereum?.isBraveWallet,
    connector: () => window.ethereum,
  },
  okx: {
    name: 'OKX Wallet',
    type: 'ethereum',
    icon: '⭕',
    color: '#000000',
    downloadUrl: 'https://www.okx.com/web3',
    detector: () => window.ethereum?.isOkxWallet || window.okxwallet,
    connector: () => window.okxwallet?.ethereum || window.ethereum,
  },
};

// Solana Network Configuration
const network = WalletAdapterNetwork.Mainnet;

// EVM Wallet Detection (including multi-protocol wallets)
const detectEVMWallets = () => {
  const wallets = [];
  
  if (window.ethereum) {
    // Specific wallet detection
    if (window.ethereum.isMetaMask) wallets.push({ name: 'MetaMask', key: 'metamask' });
    if (window.ethereum.isCoinbaseWallet) wallets.push({ name: 'Coinbase Wallet', key: 'coinbase' });
    if (window.ethereum.isTrust) wallets.push({ name: 'Trust Wallet', key: 'trust' });
    if (window.ethereum.isRainbow) wallets.push({ name: 'Rainbow', key: 'rainbow' });
    if (window.ethereum.isBraveWallet) wallets.push({ name: 'Brave Wallet', key: 'brave' });
    if (window.ethereum.isOkxWallet || window.okxwallet) wallets.push({ name: 'OKX Wallet', key: 'okx' });
    
    // Multi-protocol wallets that support EVM
    if (window.ethereum.isMPCVault) wallets.push({ name: 'MPCVault', key: 'mpcvault' });
    if (window.ethereum.isPortal) wallets.push({ name: 'Portal', key: 'portal' });
    if (window.ethereum.isCapsule) wallets.push({ name: 'Capsule', key: 'capsule' });
    if (window.ethereum.isParticle) wallets.push({ name: 'Particle Network', key: 'particle' });
    if (window.ethereum.isMagic) wallets.push({ name: 'Magic Wallet', key: 'magic' });
    if (window.ethereum.isFireblocks) wallets.push({ name: 'Fireblocks', key: 'fireblocks' });
    
    // Generic Ethereum wallet if no specific one detected
    if (wallets.length === 0) {
      wallets.push({ name: 'Ethereum Wallet', key: 'ethereum' });
    }
  }
  
  // Check for additional unlisted EVM wallets
  if (window.ethereum && window.ethereum.providers) {
    // Multiple wallet providers detected
    window.ethereum.providers.forEach((provider, index) => {
      if (!wallets.some(w => w.key === `generic-${index}`)) {
        wallets.push({ 
          name: `Wallet ${index + 1}`, 
          key: `generic-${index}`,
          provider: provider
        });
      }
    });
  }
  
  return wallets;
};

// Multi-Protocol Wallet Detection
const detectMultiProtocolWallets = () => {
  const multiWallets = [];
  
  // Check for wallets that support both Solana and EVM
  if (window.mpcvault) {
    multiWallets.push({ 
      name: 'MPCVault', 
      key: 'mpcvault', 
      protocols: ['solana', 'ethereum'],
      icon: '🔐'
    });
  }
  
  if (window.portal) {
    multiWallets.push({ 
      name: 'Portal', 
      key: 'portal', 
      protocols: ['solana', 'ethereum'],
      icon: '🌀'
    });
  }
  
  if (window.capsule) {
    multiWallets.push({ 
      name: 'Capsule', 
      key: 'capsule', 
      protocols: ['solana', 'ethereum'],
      icon: '💊'
    });
  }
  
  if (window.particle) {
    multiWallets.push({ 
      name: 'Particle Network', 
      key: 'particle', 
      protocols: ['solana', 'ethereum'],
      icon: '⚛️'
    });
  }
  
  if (window.magic) {
    multiWallets.push({ 
      name: 'Magic Wallet', 
      key: 'magic', 
      protocols: ['solana', 'ethereum'],
      icon: '🪄'
    });
  }
  
  if (window.fireblocks) {
    multiWallets.push({ 
      name: 'Fireblocks', 
      key: 'fireblocks', 
      protocols: ['solana', 'ethereum'],
      icon: '🔥'
    });
  }
  
  if (window.okxwallet) {
    multiWallets.push({ 
      name: 'OKX Wallet', 
      key: 'okx', 
      protocols: ['solana', 'ethereum'],
      icon: '⭕'
    });
  }
  
  return multiWallets;
};

// Generic/Unknown Wallet Detection
const detectGenericWallets = () => {
  const genericWallets = [];
  
  // Check for Solana wallets that aren't specifically listed
  if (window.solana && !window.solana.isPhantom) {
    // Generic Solana wallet detected
    genericWallets.push({
      name: 'Unknown Solana Wallet',
      key: 'generic-solana',
      type: 'solana',
      icon: '◎',
      protocols: ['solana']
    });
  }
  
  // Check for additional Solana wallet objects
  Object.keys(window).forEach(key => {
    try {
      if (key.toLowerCase().includes('solana') && 
          typeof window[key] === 'object' && 
          window[key]?.connect &&
          key !== 'solana') {
        genericWallets.push({
          name: `${key.charAt(0).toUpperCase() + key.slice(1)} Wallet`,
          key: `generic-${key}`,
          type: 'solana',
          icon: '◎',
          protocols: ['solana'],
          provider: window[key]
        });
      }
    } catch (error) {
      // Skip if cross-origin or access error
      console.debug(`Skipping Solana wallet detection for ${key}`);
    }
  });
  
  // Check for EVM wallets with ethereum providers
  if (window.ethereum) {
    // Check if it's an unlisted wallet by looking for unique identifiers
    const knownWallets = ['isMetaMask', 'isCoinbaseWallet', 'isTrust', 'isRainbow', 'isBraveWallet', 'isOkxWallet'];
    const hasKnownIdentifier = knownWallets.some(identifier => window.ethereum[identifier]);
    
    if (!hasKnownIdentifier) {
      genericWallets.push({
        name: 'Unknown Ethereum Wallet',
        key: 'generic-ethereum',
        type: 'ethereum',
        icon: 'Ξ',
        protocols: ['ethereum']
      });
    }
  }
  
  // Check for wallet objects that might support multiple chains
  // Use try-catch to avoid cross-origin security errors
  Object.keys(window).forEach(key => {
    try {
      const obj = window[key];
      
      // Skip if not an object or is a known wallet
      if (!obj || typeof obj !== 'object') return;
      if (['ethereum', 'solana', 'mpcvault', 'portal', 'capsule', 'particle', 'magic', 'fireblocks', 'okxwallet'].includes(key.toLowerCase())) return;
      
      // Try to access properties safely
      let hasEthereum = false;
      let hasSolana = false;
      
      try {
        hasEthereum = !!obj.ethereum;
      } catch (e) {
        // Cross-origin access blocked
      }
      
      try {
        hasSolana = !!obj.solana;
      } catch (e) {
        // Cross-origin access blocked
      }
      
      if (hasEthereum || hasSolana) {
        const protocols = [];
        if (hasEthereum) protocols.push('ethereum');
        if (hasSolana) protocols.push('solana');
        
        if (protocols.length > 0) {
          genericWallets.push({
            name: `${key.charAt(0).toUpperCase() + key.slice(1)} Wallet`,
            key: `generic-${key.toLowerCase()}`,
            type: protocols.length > 1 ? 'multi' : protocols[0],
            icon: protocols.length > 1 ? '🌐' : (protocols[0] === 'solana' ? '◎' : 'Ξ'),
            protocols: protocols,
            provider: obj
          });
        }
      }
    } catch (error) {
      // Skip this key if we can't access it (cross-origin or other security error)
      console.debug(`Skipping wallet detection for ${key}:`, error.message);
    }
  });
  
  return genericWallets;
};

// Universal Wallet Context Component
const UniversalWalletProvider = ({ children }) => {
  const [walletType, setWalletType] = useState(null); // 'solana', 'ethereum', or 'abstract'
  const [evmWallet, setEvmWallet] = useState(null);
  const [evmConnected, setEvmConnected] = useState(false);
  const [evmConnecting, setEvmConnecting] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null); // Track which network was selected
  const [multiProtocolWallet, setMultiProtocolWallet] = useState(null); // For OKX, MPCVault, etc.
  const [jwtToken, setJwtToken] = useState(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Load JWT from localStorage on mount
  useEffect(() => {
    const storedJWT = localStorage.getItem('boneforge_jwt');
    if (storedJWT) {
      setJwtToken(storedJWT);
      console.log('✅ Loaded existing JWT from localStorage');
    }
  }, []);
  
  // Solana wallet from adapter
  const solanaWallet = useSolanaWallet();
  
  // JWT Authentication function
  const authenticateWithJWT = async (walletAddress) => {
    try {
      setAuthenticating(true);
      setAuthError(null);
      
      // Create message with timestamp
      const timestamp = new Date().toISOString();
      const message = `🔐 Sign into Solana Deads - GraveForge\n⏰ Time: ${timestamp}\n✅ This signature does NOT authorize any transactions`;
      
      console.log('📝 Requesting signature for authentication...');
      
      // Sign the message
      const encodedMessage = new TextEncoder().encode(message);
      const signature = await solanaWallet.signMessage(encodedMessage);
      
      console.log('✍️ Message signed, calling auth API...');
      
      // Call auth API to generate JWT
      const response = await fetch(
        `${import.meta.env.VITE_SERVER_URL}/graveforge/auth/generate-jwt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            walletAddress,
            message,
            signature: bs58.encode(signature),
            walletType: 'solana'
          })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Authentication failed');
      }
      
      const { jwt, expiresIn } = await response.json();
      
      // Store JWT in localStorage (will be used by fetch interceptor)
      setSupabaseJWT(jwt);
      setJwtToken(jwt);
      
      return jwt;
    } catch (error) {
      console.error('❌ Authentication failed:', error);
      setAuthError(error.message);
      throw error;
    } finally {
      setAuthenticating(false);
    }
  };
  
  // Auto-set wallet type when Solana wallet connects
  useEffect(() => {
    if (solanaWallet.connected && solanaWallet.publicKey) {
      console.log('🔗 Solana wallet connected, setting walletType to solana');
      setWalletType('solana');
      
      const walletAddress = solanaWallet.publicKey.toString();
      
      // Handle referral attribution
      handleWalletConnection(walletAddress, 'boneforge').catch(err => {
        console.error('[GraveForge Referral] Error handling wallet connection:', err);
      });
      
      // Authenticate with JWT only if we don't already have one
      if (!jwtToken && solanaWallet.signMessage) {
        console.log('🔐 No JWT found, requesting authentication...');
        authenticateWithJWT(walletAddress).catch(err => {
          console.error('[GraveForge Auth] JWT authentication failed:', err);
          // Don't block wallet connection if JWT auth fails
        });
      } else if (jwtToken) {
        console.log('✅ JWT already exists, skipping authentication');
      } else {
        console.warn('⚠️ Wallet does not support message signing, skipping JWT auth');
      }
    } else if (!solanaWallet.connected && walletType === 'solana') {
      console.log('🔌 Solana wallet disconnected, clearing walletType');
      setWalletType(null);
      setJwtToken(null);
      
      // Clear JWT from localStorage
      setSupabaseJWT(null);
      
      // Clear Supabase session
      signOutSupabase();
    }
  }, [solanaWallet.connected, solanaWallet.publicKey, solanaWallet.signMessage, jwtToken]);
  
  // EVM wallet connection (including multi-protocol wallets)
  const connectEVMWallet = async (walletKey = null) => {
    if (!window.ethereum) {
      throw new Error('No Ethereum wallet detected');
    }
    
    try {
      setEvmConnecting(true);
      
      // For multi-protocol wallets, we might need to specify the chain
      let provider = window.ethereum;
      
      // Handle specific multi-protocol wallet connections
      if (walletKey === 'mpcvault' && window.mpcvault) {
        provider = window.mpcvault.ethereum || window.ethereum;
      } else if (walletKey === 'portal' && window.portal) {
        provider = window.portal.ethereum || window.ethereum;
      } else if (walletKey === 'capsule' && window.capsule) {
        provider = window.capsule.ethereum || window.ethereum;
      } else if (walletKey === 'particle' && window.particle) {
        provider = window.particle.ethereum || window.ethereum;
      } else if (walletKey === 'magic' && window.magic) {
        provider = window.magic.ethereum || window.ethereum;
      } else if (walletKey === 'fireblocks' && window.fireblocks) {
        provider = window.fireblocks.ethereum || window.ethereum;
      } else if (walletKey === 'okx' && window.okxwallet) {
        provider = window.okxwallet.ethereum || window.ethereum;
      }
      
      const accounts = await provider.request({
        method: 'eth_requestAccounts'
      });
      
      if (accounts.length > 0) {
        const walletAddress = accounts[0];
        setEvmWallet({ address: walletAddress, provider, walletKey });
        setEvmConnected(true);
        setWalletType('ethereum');
        
        // Handle referral attribution
        handleWalletConnection(walletAddress, 'boneforge').catch(err => {
          console.error('[GraveForge Referral] Error handling wallet connection:', err);
        });
        
        // Authenticate with JWT if we don't already have one
        if (!jwtToken) {
          console.log('🔐 No JWT found, requesting EVM authentication...');
          setAuthenticating(true);
          try {
            // Create message with timestamp
            const timestamp = new Date().toISOString();
            const message = `🔐 Sign into Solana Deads - GraveForge\n⏰ Time: ${timestamp}\n✅ This signature does NOT authorize any transactions`;
            
            console.log('📝 Requesting signature for authentication...');
            
            // Sign the message with EVM wallet (EIP-191 personal_sign)
            const signature = await provider.request({
              method: 'personal_sign',
              params: [message, walletAddress]
            });
            
            console.log('✍️ Message signed, calling auth API...');
            
            // Call auth API to generate JWT
            const authResponse = await fetch(
              `${import.meta.env.VITE_SERVER_URL}/graveforge/auth/generate-jwt`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  walletAddress,
                  message,
                  signature,
                  walletType: 'ethereum'
                })
              }
            );
            
            if (!authResponse.ok) {
              const error = await authResponse.json();
              throw new Error(error.error || 'Authentication failed');
            }
            
            const { jwt } = await authResponse.json();
            
            // Store JWT in localStorage
            setSupabaseJWT(jwt);
            setJwtToken(jwt);
            console.log('✅ EVM JWT authentication successful');
          } catch (error) {
            console.error('❌ EVM JWT authentication failed:', error);
            setAuthError(error.message);
            // Don't block wallet connection if JWT auth fails
          } finally {
            setAuthenticating(false);
          }
        } else {
          console.log('✅ JWT already exists, skipping authentication');
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setEvmConnecting(false);
    }
  };

  // Generic wallet connection
  const connectGenericWallet = async (walletKey, protocol) => {
    const genericWallets = detectGenericWallets();
    const wallet = genericWallets.find(w => w.key === walletKey);
    
    if (!wallet) {
      throw new Error('Generic wallet not found');
    }
    
    if (protocol === 'solana' || wallet.type === 'solana') {
      try {
        setWalletType('solana');
        const provider = wallet.provider || window.solana;
        const response = await provider.connect();
        if (response.publicKey) {
          console.log(`${wallet.name} Solana connected:`, response.publicKey.toString());
        }
      } catch (error) {
        console.error(`${wallet.name} Solana connection failed:`, error);
        throw error;
      }
    } else if (protocol === 'ethereum' || wallet.type === 'ethereum') {
      return await connectEVMWallet(walletKey);
    }
  };

  // Multi-protocol wallet connection
  const connectMultiProtocolWallet = async (walletKey, protocol) => {
    // Check if it's a generic wallet first
    const genericWallets = detectGenericWallets();
    const genericWallet = genericWallets.find(w => w.key === walletKey);
    
    if (genericWallet) {
      return await connectGenericWallet(walletKey, protocol);
    }
    
    if (protocol === 'solana') {
      // For Solana connection on multi-protocol wallets
      if (walletKey === 'mpcvault' && window.mpcvault?.solana) {
        try {
          setWalletType('solana');
          const response = await window.mpcvault.solana.connect();
          if (response.publicKey) {
            // This would integrate with the Solana wallet adapter
            console.log('MPCVault Solana connected:', response.publicKey.toString());
          }
        } catch (error) {
          console.error('MPCVault Solana connection failed:', error);
          throw error;
        }
      } else if (walletKey === 'okx' && window.okxwallet?.solana) {
        try {
          setWalletType('solana');
          const response = await window.okxwallet.solana.connect();
          if (response.publicKey) {
            const walletAddress = response.publicKey.toString();
            console.log('OKX Solana connected:', walletAddress);
            
            // Store wallet info
            setMultiProtocolWallet({
              address: walletAddress,
              publicKey: response.publicKey,
              provider: window.okxwallet.solana,
              walletKey: 'okx',
              name: 'OKX Wallet'
            });
            
            // Handle referral attribution
            handleWalletConnection(walletAddress, 'boneforge').catch(err => {
              console.error('[GraveForge Referral] Error handling wallet connection:', err);
            });
            
            // Authenticate with JWT if we don't already have one
            if (!jwtToken && window.okxwallet.solana.signMessage) {
              console.log('🔐 No JWT found, requesting OKX authentication...');
              setAuthenticating(true);
              try {
                // Create message with timestamp
                const timestamp = new Date().toISOString();
                const message = `🔐 Sign into Solana Deads - GraveForge\n⏰ Time: ${timestamp}\n✅ This signature does NOT authorize any transactions`;
                
                console.log('📝 Requesting signature for authentication...');
                
                // Sign the message with OKX
                const encodedMessage = new TextEncoder().encode(message);
                const signResult = await window.okxwallet.solana.signMessage(encodedMessage);
                
                console.log('✍️ Message signed, calling auth API...');
                
                // Call auth API to generate JWT
                const authResponse = await fetch(
                  `${import.meta.env.VITE_SERVER_URL}/graveforge/auth/generate-jwt`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      walletAddress,
                      message,
                      signature: bs58.encode(signResult.signature),
                      walletType: 'solana'
                    })
                  }
                );
                
                if (!authResponse.ok) {
                  const error = await authResponse.json();
                  throw new Error(error.error || 'Authentication failed');
                }
                
                const { jwt } = await authResponse.json();
                
                // Store JWT in localStorage
                setSupabaseJWT(jwt);
                setJwtToken(jwt);
                console.log('✅ JWT authentication successful');
              } catch (error) {
                console.error('❌ OKX JWT authentication failed:', error);
                setAuthError(error.message);
                // Don't block wallet connection if JWT auth fails
              } finally {
                setAuthenticating(false);
              }
            } else if (jwtToken) {
              console.log('✅ JWT already exists, skipping authentication');
            } else {
              console.warn('⚠️ OKX wallet does not support message signing, skipping JWT auth');
            }
          }
        } catch (error) {
          console.error('OKX Solana connection failed:', error);
          throw error;
        }
      }
      // Add other multi-protocol Solana connections here
    } else if (protocol === 'ethereum') {
      return await connectEVMWallet(walletKey);
    }
  };
  
  const disconnectEVMWallet = () => {
    setEvmWallet(null);
    setEvmConnected(false);
    if (walletType === 'ethereum') {
      setWalletType(null);
    }
  };
  
  // Universal wallet interface
  const wallet = walletType === 'solana' ? 
    (multiProtocolWallet || (solanaWallet.publicKey ? { address: solanaWallet.publicKey.toString() } : null)) :
    evmWallet;
    
  // Simplified connected state - check Solana, EVM, and multi-protocol
  const connected = solanaWallet.connected || evmConnected || (multiProtocolWallet !== null);
  const connecting = solanaWallet.connecting || evmConnecting;
  
  // Determine wallet address - prioritize multi-protocol wallet for OKX, etc.
  const walletAddress = multiProtocolWallet?.address || solanaWallet.publicKey?.toString() || evmWallet?.address || null;
  
  const connect = async (type = 'auto', network = null) => {
    if (type === 'solana' || (type === 'auto' && solanaWallet.wallets.length > 0)) {
      setWalletType('solana');
      setSelectedNetwork('solana');
      return await solanaWallet.select();
    } else if (type === 'ethereum' || (type === 'auto' && window.ethereum)) {
      // Set wallet type based on selected network
      if (network === 'abstract') {
        setWalletType('abstract');
        setSelectedNetwork('abstract');
      } else {
        setWalletType('ethereum');
        setSelectedNetwork('ethereum');
      }
      return await connectEVMWallet();
    } else {
      throw new Error('No compatible wallet found');
    }
  };
  
  const disconnect = async () => {
    if (walletType === 'solana') {
      if (multiProtocolWallet) {
        // Disconnect multi-protocol wallet (OKX, etc.)
        try {
          if (multiProtocolWallet.provider?.disconnect) {
            await multiProtocolWallet.provider.disconnect();
          }
        } catch (error) {
          console.error('Error disconnecting multi-protocol wallet:', error);
        }
        setMultiProtocolWallet(null);
      } else {
        await solanaWallet.disconnect();
      }
    } else if (walletType === 'ethereum') {
      disconnectEVMWallet();
    }
    setWalletType(null);
    setJwtToken(null);
    
    // Clear JWT from localStorage
    setSupabaseJWT(null);
  };
  
  const value = {
    // Universal interface
    wallet,
    walletAddress: walletAddress,  // Use the computed walletAddress
    connected,
    connecting,
    walletType: walletType || (solanaWallet.connected ? 'solana' : evmConnected ? 'ethereum' : null),
    selectedNetwork: selectedNetwork || walletType,
    walletName: multiProtocolWallet?.name || solanaWallet.wallet?.adapter?.name || (evmConnected ? (walletType === 'abstract' ? 'Abstract Wallet' : 'Ethereum Wallet') : null),
    
    // Connection functions
    connect,
    disconnect,
    
    // Solana specific
    solanaWallet,
    
    // EVM specific  
    evmWallet,
    connectEVMWallet,
    disconnectEVMWallet,
    
    // Multi-protocol specific
    connectMultiProtocolWallet,
    connectGenericWallet,
    
    // Available wallets
    availableSolanaWallets: solanaWallet.wallets || [],
    availableEVMWallets: detectEVMWallets(),
    availableMultiProtocolWallets: detectMultiProtocolWallets(),
    availableGenericWallets: detectGenericWallets(),
    
    // JWT Authentication
    jwtToken,
    authenticating,
    authError,
    authenticateWithJWT,
    
    // Legacy compatibility
    connectSolanaWallet: () => connect('solana'),
    connectEthereumWallet: () => connect('ethereum'),
    disconnectWallet: disconnect,
    connectionStatus: connected ? 'connected' : (connecting ? 'connecting' : 'disconnected'),
    error: solanaWallet.error?.message || authError || null
  };
  
  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Main WalletProvider that wraps everything
export const WalletProvider = ({ children }) => {
  // Solana wallet adapters
  const wallets = useMemo(() => createWalletAdapters(), []);
  
  const endpoint = useMemo(() => clusterApiUrl(network), []);

  // Detect if we're in a mobile wallet browser (Phantom, OKX, etc.)
  const isMobileWalletBrowser = useMemo(() => {
    // Check for mobile wallet browser indicators
    const isPhantomMobile = window.solana?.isPhantom && /Mobile|Android|iPhone/i.test(navigator.userAgent);
    const isOKXMobile = (window.okxwallet || window.okex) && /Mobile|Android|iPhone/i.test(navigator.userAgent);
    const isMobile = /Mobile|Android|iPhone/i.test(navigator.userAgent);
    
    // If on mobile and has a wallet provider, likely in-app browser
    return isMobile && (window.solana || window.ethereum);
  }, []);

  console.log('🔍 Mobile wallet browser detected:', isMobileWalletBrowser);
  
  // For mobile wallet browsers, we need to ensure the wallet is in the adapters list
  // even if it's not explicitly imported
  const mobileWallets = useMemo(() => {
    if (!isMobileWalletBrowser) return wallets;
    
    // If we're in a mobile wallet browser, ensure we have the standard adapter
    // The wallet will be auto-detected by the adapter
    return wallets;
  }, [isMobileWalletBrowser, wallets]);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <SolanaWalletProvider 
        wallets={mobileWallets} 
        autoConnect={true}
        localStorageKey="boneforge-wallet"
        onError={(error) => {
          console.error('Wallet adapter error:', error);
        }}
      >
        <UniversalWalletProvider>
          {children}
        </UniversalWalletProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
};
